import React, { useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import Button1 from "../../form/Button1";
import StaffApiErrorModal, {
  getStaffApiErrorCopy,
} from "../StaffApiErrorModal";
import denyChangeTicketRequest from "../../../controllers/booking/postTicketing/change/denyChangeTicketRequest";
import declineChangeBySupplier from "../../../controllers/booking/postTicketing/change/declineChangeBySupplier";
import addChangeMessage from "../../../controllers/booking/postTicketing/change/addMessage";
import { getChangeKindLabel } from "../../../data/flight/order/getChangeAdminGuidance";

export const CHANGE_SUPPLIER_OUTCOME = {
  decline: "supplier_decline",
  failure: "supplier_failure",
  technical: "technical_failure",
};

/**
 * Task 8 — record airline decline vs failure vs technical error.
 * Technical must NOT use supplier-decline status (remark only + keep retry path).
 */
export default function ChangeSupplierOutcomeForm({
  data,
  initialKind = CHANGE_SUPPLIER_OUTCOME.decline,
  callback,
  close,
}) {
  const [kind, setKind] = useState(initialKind);
  const [reason, setReason] = useState("");
  const [supplierResponse, setSupplierResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [apiError, setApiError] = useState(null);
  const label = getChangeKindLabel(data);

  useEffect(() => {
    setKind(initialKind);
  }, [initialKind]);

  async function handleSubmit() {
    if (!reason.trim()) {
      enqueueSnackbar("Enter a reason.", { variant: "warning" });
      return;
    }

    setLoading(true);
    try {
      if (kind === CHANGE_SUPPLIER_OUTCOME.technical) {
        const note = [
          "[TECHNICAL_FAILURE — not an airline decline]",
          reason.trim(),
          supplierResponse.trim()
            ? `Supplier/API response: ${supplierResponse.trim()}`
            : null,
        ]
          .filter(Boolean)
          .join("\n");
        const res = await addChangeMessage(data?._id, { message: note });
        if (res.return) {
          enqueueSnackbar(
            "Technical failure recorded. Status stays for retry — this is not an airline decline.",
            { variant: "success" },
          );
          callback?.();
          close?.();
        } else setApiError(getStaffApiErrorCopy(res));
        return;
      }

      if (kind === CHANGE_SUPPLIER_OUTCOME.decline) {
        const body = { reason: reason.trim() };
        if (supplierResponse.trim()) {
          body.supplierResponse = supplierResponse.trim();
        }
        const res = await declineChangeBySupplier(data?._id, body);
        if (res.return) {
          const warnCode =
            res?.warning?.code ?? res?.warning?.errorCode ?? res?.warning;
          if (warnCode === 827 || warnCode === "827") {
            enqueueSnackbar(
              "Airline decline recorded, but returning the agent payment failed. Record the decline again to retry the return.",
              { variant: "warning" },
            );
          } else {
            enqueueSnackbar(
              "Airline decline recorded. Original ticket is unchanged.",
              { variant: "success" },
            );
          }
          callback?.();
          close?.();
        } else setApiError(getStaffApiErrorCopy(res));
        return;
      }

      const res = await denyChangeTicketRequest(data?._id, {
        rejectReason: reason.trim(),
        outcome: "FailedBySupplier",
        declineKind: kind,
        lastErrorKind: "supplier_failure",
        supplierResponse: supplierResponse.trim() || undefined,
      });
      if (res.return) {
        enqueueSnackbar(
          "Supplier failure recorded. Original ticket is unchanged.",
          { variant: "success" },
        );
        callback?.();
        close?.();
      } else setApiError(getStaffApiErrorCopy(res));
    } finally {
      setLoading(false);
    }
  }

  return (
    <>
      <div className="flex flex-col gap-4 max-w-[520px] p-1">
        <div>
          <h5 className="text-base font-semibold text-gray-900">
            Record airline / supplier result
          </h5>
          <p className="text-sm text-gray-600 mt-1">
            Use this after you worked this {label} with the airline. Do not mark
            an API timeout as an airline decline.
          </p>
        </div>

        <div className="flex flex-col gap-2">
          {[
            {
              value: CHANGE_SUPPLIER_OUTCOME.decline,
              title: "Airline declined",
              text: "Airline said no. Original ticket stays. Closes the request.",
            },
            {
              value: CHANGE_SUPPLIER_OUTCOME.failure,
              title: "Supplier / airline failure",
              text: "Airline or supplier failed the change (not a timeout). Original ticket stays.",
            },
            {
              value: CHANGE_SUPPLIER_OUTCOME.technical,
              title: "Technical / API failure",
              text: "Timeout, API down, or key error. Does not close as decline — you can retry send.",
            },
          ].map((opt) => (
            <label
              key={opt.value}
              className={`flex gap-3 rounded-xl border px-3 py-2.5 cursor-pointer ${
                kind === opt.value
                  ? "border-primary1 bg-primary1/5"
                  : "border-[#E8EEF5] bg-white"
              }`}
            >
              <input
                type="radio"
                className="mt-1"
                checked={kind === opt.value}
                onChange={() => setKind(opt.value)}
              />
              <span>
                <span className="block text-sm font-semibold text-gray-900">
                  {opt.title}
                </span>
                <span className="block text-xs text-gray-600 mt-0.5">
                  {opt.text}
                </span>
              </span>
            </label>
          ))}
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
            Reason
          </label>
          <textarea
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            rows={3}
            className="mt-1 w-full rounded-lg border border-[#DBDBDB] px-2.5 py-2 text-sm"
            placeholder="Why this outcome?"
          />
        </div>

        <div>
          <label className="text-[10px] uppercase tracking-wide text-[#94A3B8] font-semibold">
            Supplier response / error (optional)
          </label>
          <textarea
            value={supplierResponse}
            onChange={(e) => setSupplierResponse(e.target.value)}
            rows={2}
            className="mt-1 w-full rounded-lg border border-[#DBDBDB] px-2.5 py-2 text-sm"
            placeholder="Paste airline / GDS / API message…"
          />
        </div>

        <div className="flex justify-end gap-2">
          {close ? (
            <Button1 variant="outlined" onClick={() => close()}>
              Cancel
            </Button1>
          ) : null}
          <Button1 loading={loading} onClick={handleSubmit}>
            Save result
          </Button1>
        </div>
      </div>
      <StaffApiErrorModal
        open={Boolean(apiError)}
        setOpen={(next) => {
          if (!next) setApiError(null);
        }}
        title={apiError?.title}
        text={apiError?.text}
      />
    </>
  );
}
